/**
 * Initialises the Sentry browser SDK once the contrail script has loaded
 * and sets tags useful for understanding real-world client behaviour,
 * particularly which Office.js requirement sets are supported.
 *
 * Uses @sentry/browser v7's getCurrentHub().setTag() for scope-level tags.
 * Session tracking is handled automatically by autoSessionTracking — the SDK
 * listens for visibilitychange/pagehide internally, which works correctly in
 * the Outlook add-in iframe even when the user closes the task pane.
 */
let sentryInitialized = false;

const configureSentry = () => {
  if (sentryInitialized) return;
  const dsn = window.SENTRY_DSN;
  if (!dsn || !window.Sentry) return;

  sentryInitialized = true;
  console.log("[Sentry] Initializing with environment:", window.SENTRY_ENVIRONMENT);
  window.Sentry.init({
    dsn,
    environment: window.SENTRY_ENVIRONMENT,
    autoSessionTracking: true,
    release: window.SENTRY_RELEASE,
  });
  console.log("[Sentry] Initialized");

  const hub = window.Sentry.getCurrentHub();

  // Platform — office365 or gmail, derived from the URL search params
  const platform = new URL(window.location.href).searchParams.get("platform") ?? "unknown";
  hub.setTag("platform", platform);

  // Office.js context
  if (typeof Office !== "undefined" && Office.context) {
    if (Office.context.diagnostics) {
      hub.setTag("outlookHost", Office.context.diagnostics.host);
      hub.setTag("outlookPlatform", Office.context.diagnostics.platform);
      hub.setTag("outlookVersion", Office.context.diagnostics.version);
    }

    if (Office.context.requirements) {
      const versions = ["1.1", "1.2", "1.3", "1.4", "1.5", "1.6", "1.7", "1.8", "1.9", "1.10", "1.11", "1.12", "1.13", "1.14", "1.15", "1.16"];
      let highest = "none";
      for (const v of versions) {
        if (Office.context.requirements.isSetSupported("Mailbox", v)) {
          highest = v;
        }
      }
      hub.setTag("mailboxRequirementSet", highest);
      hub.setTag("emlSubmissionSupported", String(Office.context.requirements.isSetSupported("Mailbox", "1.14")));
      hub.setTag("internetHeadersSupported", String(Office.context.requirements.isSetSupported("Mailbox", "1.8")));
    }
  }
};

export const initSentry = () => {
  // If the contrail script already loaded and fired sentry:ready before React
  // hydrated and this listener was registered, window.Sentry will already be
  // set — call configureSentry directly in that case.
  if (window.Sentry) {
    configureSentry();
  } else {
    window.addEventListener("sentry:ready", configureSentry, { once: true });
  }
};
